import { Navigate, Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import type { Feature } from '../../services/auth/permissions';

interface PermissionGuardProps {
  feature: Feature;
  /**
   * If provided, render children when allowed — otherwise render <Outlet />.
   * This lets the guard work both as a route wrapper and as an inline gate.
   */
  children?: ReactNode;
  /**
   * Route to redirect to when access is denied.
   * Defaults to /dashboard.
   */
  redirectTo?: string;
  /**
   * Custom UI to render instead of redirecting when access is denied.
   * Useful for nested settings tabs where we want a message, not a navigation.
   */
  fallback?: ReactNode;
}

/**
 * Gates a route or component behind a feature-based permission check.
 *
 * --- Route wrapper (renders <Outlet />) ---
 *   <Route element={<PermissionGuard feature="settings_branding" />}>
 *     <Route path="branding" element={<BrandingTab />} />
 *   </Route>
 *
 * --- Inline gate (renders children) ---
 *   <PermissionGuard feature="invoices_create">
 *     <CreateInvoiceButton />
 *   </PermissionGuard>
 *
 * --- Inline gate with custom fallback (no redirect) ---
 *   <PermissionGuard feature="settings_firm" fallback={<AccessDenied />}>
 *     <FirmSettingsTab />
 *   </PermissionGuard>
 */
export function PermissionGuard({
  feature,
  children,
  redirectTo = '/dashboard',
  fallback,
}: PermissionGuardProps) {
  const canAccess = useCanAccessFeature(feature);

  if (!canAccess) {
    if (fallback !== undefined) return <>{fallback}</>;
    return <Navigate to={redirectTo} replace />;
  }

  return children !== undefined ? <>{children}</> : <Outlet />;
}
